import { useCallback, useEffect, useState } from "react";

import { SCREENS } from "@tsf/tailwind-config/lib/constants";

import { useMediaScreenContext } from "~/contexts/madia-screen";

export function useMediaQuery(query: string): boolean {
  const getMatches = (query: string): boolean => {
    // Prevents SSR issues
    if (typeof window !== "undefined") {
      return window.matchMedia(query).matches;
    }
    return false;
  };

  const [matches, setMatches] = useState<boolean>(() => getMatches(query));

  function handleChange() {
    setMatches(getMatches(query));
  }

  useEffect(() => {
    const matchMedia = window.matchMedia(query);

    // Triggered at the first client-side load and if query changes
    handleChange();

    // Listen matchMedia
    if (matchMedia.addListener) {
      matchMedia.addListener(handleChange);
    } else {
      matchMedia.addEventListener("change", handleChange);
    }

    return () => {
      if (matchMedia.removeListener) {
        matchMedia.removeListener(handleChange);
      } else {
        matchMedia.removeEventListener("change", handleChange);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  return matches;
}
export const usePick = () => useMediaScreenContext().pick;
export const useMediaScreen = () => {
  const sm = useMediaQuery(`(max-width: ${SCREENS.sm})`);
  const md = useMediaQuery(
    `(min-width: ${SCREENS.sm}) and (max-width: ${SCREENS.md})`,
  );
  const lg = useMediaQuery(
    `(min-width: ${SCREENS.md}) and (max-width: ${SCREENS.lg})`,
  );
  const xl = useMediaQuery(`(min-width: ${SCREENS.lg}) `);
  const xxl = useMediaQuery(`(min-width: ${SCREENS["xl"]}) `);

  const pick = useCallback(
    <T,>([_sm, _md, _lg, _xl, _xxl]: [sm: T, md?: T, lg?: T, xl?: T, xxl?: T]):
      | T
      | undefined => {
      if (xxl) return _xxl ?? _xl ?? _lg ?? _md ?? _sm;
      if (xl) return _xl ?? _lg ?? _md ?? _sm;
      if (lg) return _lg ?? _md ?? _sm;
      if (md) return _md ?? _sm;
      if (sm) return _sm;
      return _sm;
    },
    [sm, md, lg, xl, xxl],
  );
  return {
    is: sm ? "sm" : md ? "md" : lg ? "lg" : xl ? "xl" : "xxl",
    isMobile: sm,
    isTablet: lg,
    isDesktop: xl,

    pick,
  } satisfies {
    is: "sm" | "md" | "lg" | "xl" | "xxl";
    isMobile: boolean;
    isTablet: boolean;
    isDesktop: boolean;
    pick: <T>([sm, md, lg, xl, xxl]: [
      sm: T,
      md?: T,
      lg?: T,
      xl?: T,
      xxl?: T,
    ]) => T | undefined;
  };
};
